import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';

interface JoystickProps {
  onMove: (x: number, y: number) => void;
  onEnd: () => void;
  visible: boolean;
}

export const Joystick = ({ onMove, onEnd, visible }: JoystickProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const touchIdRef = useRef<number | null>(null);
  const centerRef = useRef<{ x: number; y: number } | null>(null);

  const maxDistance = 50; // Maximum distance the stick can move from center

  useEffect(() => {
    if (!containerRef.current) return;

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const touch = e.touches[0];
      touchIdRef.current = touch.identifier;

      // Store the center position once at the start
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        centerRef.current = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
      }

      setActive(true);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchIdRef.current === null || !centerRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const touch = Array.from(e.touches).find((t) => t.identifier === touchIdRef.current);
      if (!touch) return;

      let deltaX = touch.clientX - centerRef.current.x;
      let deltaY = touch.clientY - centerRef.current.y;

      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      if (distance > maxDistance) {
        const angle = Math.atan2(deltaY, deltaX);
        deltaX = Math.cos(angle) * maxDistance;
        deltaY = Math.sin(angle) * maxDistance;
      }

      setPosition({ x: deltaX, y: deltaY });

      // Normalize to -1 to 1 range
      const normalizedX = -deltaX / maxDistance; // Invert X for correct left/right
      const normalizedY = -deltaY / maxDistance; // Invert Y for intuitive controls

      onMove(normalizedX, normalizedY);

      // Dispatch custom event for Controls component
      window.dispatchEvent(new CustomEvent('joystick-move', { detail: { x: normalizedX, y: normalizedY } }));
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touches = Array.from(e.changedTouches);
      if (touches.some((t) => t.identifier === touchIdRef.current)) {
        e.preventDefault();
        e.stopPropagation();

        touchIdRef.current = null;
        centerRef.current = null;
        setActive(false);
        setPosition({ x: 0, y: 0 });
        onEnd();

        // Dispatch custom event for Controls component
        window.dispatchEvent(new CustomEvent('joystick-end'));
      }
    };

    const container = containerRef.current;
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [onMove, onEnd]);

  if (!visible) return null;

  return (
    <div
      ref={containerRef}
      className={cn(
        'absolute bottom-20 left-4 z-20 w-32 h-32 rounded-full transition-opacity',
        'bg-slate-200/40 dark:bg-stone-800/40 backdrop-blur-sm border-2 border-slate-300/50 dark:border-stone-700/50',
        active ? 'opacity-100' : 'opacity-60',
      )}>
      <div
        className={cn(
          'absolute w-12 h-12 rounded-full',
          'bg-slate-700/80 dark:bg-stone-300/80 border-2 border-slate-800/70 dark:border-stone-200/70',
          'shadow-lg',
          'pointer-events-none',
        )}
        style={{
          left: '50%',
          top: '50%',
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          transition: active ? 'none' : 'transform 0.2s ease-out',
        }}
      />
    </div>
  );
};
